import React, { useState, useEffect } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import axios from "axios";

export default function CategoryDetail() {
  const location = useLocation();
  const navigate = useNavigate();
  const [category, setCategory] = useState(null);
  const [allCategories, setAllCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch all categories
    async function fetchAllCategories() {
      try {
        const { data } = await axios.get(
          "https://api.elza2.store/api/v1/categories"
        );
        setAllCategories(data.categories);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    }

    // Get category from location state or fetch if not available
    const fetchCategory = async () => {
      try {
        setLoading(true);
        const categoryFromState = location.state?.category;

        if (categoryFromState) {
          setCategory(categoryFromState);
        } else {
          // If no category in state, you might want to fetch it based on URL params
          const categoryId = new URLSearchParams(location.search).get(
            "categoryId"
          );
          if (categoryId) {
            const { data } = await axios.get(
              `https://api.elza2.store/api/v1/categories/${categoryId}`
            );
            setCategory(data.category);
          }
        }
        setLoading(false);
      } catch (error) {
        console.error("Error fetching category details:", error);
        setLoading(false);
      }
    };

    fetchCategory();
    fetchAllCategories();
  }, [location.state, location.search]);

  if (loading) {
    return <div className="text-center py-10">Loading...</div>;
  }

  if (!category) {
    return <div className="text-center py-10">No category found</div>;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold">{category.name} Subcategories</h1>
        <button
          onClick={() => navigate(`/products?category=${category._id}`)}
          className="bg-gray-200 hover:bg-gray-300 px-4 py-2 rounded-lg transition"
        >
          Browse All {category.name}
        </button>
      </div>
      <div className="mb-4 text-center">
        <Link
          to="/custom"
          className="inline-block explore-btn px-6 py-3 rounded-lg transition-colors duration-300 shadow-md"
        >
          Create your own Custom Item ➕ 🎨
        </Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {/* Subcategories Section */}
        {category.subCategories.length > 0 ? (
          category.subCategories.map((subCategory) => (
            <Link
              key={subCategory._id}
              to={`/products?category=${category._id}&subcategory=${subCategory._id}`}
              className="bg-white shadow-lg rounded-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2"
            >
              {subCategory.image ? (
                <img
                  src={subCategory.image}
                  alt={subCategory.name}
                  className="w-full h-48 object-cover"
                />
              ) : (
                <div className="w-full h-48 bg-gray-200 flex items-center justify-center">
                  No Image
                </div>
              )}
              <div className="p-4">
                <h3 className="text-lg text-main font-semibold text-center">
                  {subCategory.name}
                </h3>
              </div>
            </Link>
          ))
        ) : (
          <div className="col-span-full text-center text-gray-500">
            No subcategories available
          </div>
        )}
      </div>

      {/* Related Categories Section */}
      <div className="mt-12">
        <h2 className="text-2xl font-bold mb-6">Related Categories</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
          {allCategories
            .filter((cat) => cat._id !== category._id)
            .map((relatedCategory) => (
              <div
                key={relatedCategory._id}
                onClick={() => {
                  if (
                    relatedCategory.subCategories &&
                    relatedCategory.subCategories.length > 0
                  ) {
                    // If subcategories exist, navigate to category detail page
                    navigate(`/category/${relatedCategory.slug}`, {
                      state: { category: relatedCategory },
                    });
                  } else {
                    // If no subcategories, directly navigate to products for this category
                    navigate(`/products?category=${relatedCategory._id}`);
                  }
                }}
                className="bg-white shadow-md rounded-lg overflow-hidden cursor-pointer hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2"
              >
                <img
                  src={relatedCategory.image}
                  alt={relatedCategory.name}
                  className="w-full h-40 object-cover"
                />
                <div className="p-3 text-center">
                  <h3 className="font-semibold">{relatedCategory.name}</h3>
                  {relatedCategory.subCategories &&
                    relatedCategory.subCategories.length > 0 && (
                      <span className="text-sm text-gray-500">
                        +{relatedCategory.subCategories.length} Subcategories
                      </span>
                    )}
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}
